import { LegalFeeItem } from '@/types/legalFees';

/**
 * Determines the paid status of a legal fee item based on the total payments made.
 * Returns 'paid' only when itemAmount > 0 and totalPaid >= itemAmount.
 */
export function computePaidStatus(
  itemAmount: number,
  totalPaid: number,
): 'paid' | 'unpaid' {
  if (itemAmount > 0 && totalPaid >= itemAmount) {
    return 'paid';
  }
  return 'unpaid';
}

/**
 * Builds the transaction description for an individual item payment.
 * Format: "Stage {stageName} fee for {itemTitle} on {propertyName}"
 */
export function buildItemPaymentDescription(
  stageName: string,
  itemTitle: string,
  propertyName: string,
): string {
  return `Stage ${stageName} fee for ${itemTitle} on ${propertyName}`;
}

/**
 * Builds the transaction description for a bulk stage payment.
 * Format: "Stage {stageName} bulk payment for {propertyName}"
 */
export function buildStagePaymentDescription(
  stageName: string,
  propertyName: string,
): string {
  return `Stage ${stageName} bulk payment for ${propertyName}`;
}

/**
 * Computes the total outstanding amount across all unpaid items,
 * converting each item's amount to the asset's base currency using the provided rates.
 *
 * @param items - Array of legal fee items
 * @param assetCurrency - The base currency of the asset
 * @param rates - Exchange rates keyed by currency code, relative to assetCurrency (e.g. { USD: 130 } means 1 USD = 130 assetCurrency units)
 */
export function computeTotalOutstanding(
  items: LegalFeeItem[],
  assetCurrency: string,
  rates: Record<string, number>,
): number {
  return items
    .filter((item) => item.paid_status === 'unpaid')
    .reduce((sum, item) => {
      if (item.currency === assetCurrency) {
        return sum + item.amount;
      }
      const rate = rates[item.currency];
      if (rate == null || rate === 0) {
        // If no rate available, skip conversion (treat as 0 contribution)
        return sum;
      }
      return sum + item.amount * rate;
    }, 0);
}

export const STAGE_TEMPLATE = [
  {
    name: 'Legal Team',
    items: [
      'Legal fees',
      'Office Disbursements',
      'VAT at 16% on legal fees',
      'Proportionate cost of registering the Corporation and the Sectional Plan',
    ],
  },
  {
    name: 'Developer',
    items: [
      'Cost of Installation of Water meters',
      'Cost of Installation of Electricity meters',
      'Service Charge Deposit',
    ],
  },
  {
    name: 'Final Completion',
    items: [
      'Stamp Duty at 4% of the Purchase price',
      'Banking charges and KRA e-slip',
      'Office Disbursements',
      'Estimate of Government valuation and registration costs',
    ],
  },
] as const;
